import React, { useState, useEffect } from "react";
import {
  User,
  Mail,
  Phone,
  CalendarDays,
  Edit2,
  Check,
  X,
  AlertCircle,
  CheckCircle2,
  MapPin,
  Plus,
  Trash2,
} from "lucide-react";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
  addDoc,
  deleteDoc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../../firebase";

const PersonalInfo = () => {
  const [user, setUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isAddingAddress, setIsAddingAddress] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [addresses, setAddresses] = useState([]);

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    phone: "",
    dob: "",
    createdAt: "",
    lastUpdated: "",
    isVerified: false,
    accountType: "",
    profileComplete: false,
  });

  const [tempData, setTempData] = useState(formData);
  const [newAddress, setNewAddress] = useState({
    label: "",
    street: "",
    city: "",
    state: "",
    pincode: "",
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (!currentUser) {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const fetchAddresses = async (userId) => {
    try {
      const addressesRef = collection(db, `users/${userId}/addresses`);
      const snapshot = await getDocs(addressesRef);
      const addressList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAddresses(addressList);
    } catch (error) {
      console.error("Error fetching addresses:", error);
      setError("Failed to load addresses. Please try again later.");
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;

      try {
        setLoading(true);
        const userDocRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          const formattedData = {
            username: userData.username || "",
            email: userData.email || user.email || "",
            phone: userData.phone || "",
            dob: userData.dob || "",
            createdAt: userData.createdAt || "",
            lastUpdated: userData.lastUpdated || "",
            isVerified: userData.isVerified || false,
            accountType: userData.accountType || "email",
            profileComplete: userData.profileComplete || false,
          };
          setFormData(formattedData);
          setTempData(formattedData);

          // Fetch addresses
          await fetchAddresses(user.uid);
        } else {
          const initialData = {
            username: user.displayName || "",
            email: user.email || "",
            phone: "",
            dob: "",
            createdAt: new Date().toISOString(),
            lastUpdated: new Date().toISOString(),
            isVerified: false,
            accountType: "email",
            profileComplete: false,
          };
          setFormData(initialData);
          setTempData(initialData);
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
        setError("Failed to load user information. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTempData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError("");
    setSuccess("");
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setNewAddress((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddAddress = async () => {
    if (
      !newAddress.street ||
      !newAddress.city ||
      !newAddress.state ||
      !newAddress.pincode
    ) {
      setError("Please fill in all address fields");
      return;
    }

    if (!/^\d{6}$/.test(newAddress.pincode)) {
      setError("Please enter a valid 6-digit pincode");
      return;
    }

    try {
      const addressesRef = collection(db, `users/${user.uid}/addresses`);
      const addressDetail = {
        label: newAddress.label || "Home",
        street: newAddress.street,
        city: newAddress.city,
        state: newAddress.state,
        pincode: newAddress.pincode,
        detail: `${newAddress.street}, ${newAddress.city}, ${newAddress.state}, ${newAddress.pincode}`,
      };

      const docRef = await addDoc(addressesRef, addressDetail);
      const newAddressObj = {
        id: docRef.id,
        ...addressDetail,
      };

      setAddresses((prev) => [...prev, newAddressObj]);
      setNewAddress({
        label: "",
        street: "",
        city: "",
        state: "",
        pincode: "",
      });
      setIsAddingAddress(false);
      setSuccess("Address added successfully!");
    } catch (error) {
      console.error("Error adding address:", error);
      setError("Failed to add address. Please try again.");
    }
  };

  const handleRemoveAddress = async (addressId) => {
    try {
      const addressRef = doc(db, `users/${user.uid}/addresses/${addressId}`);
      await deleteDoc(addressRef);
      setAddresses((prev) => prev.filter((addr) => addr.id !== addressId));
      setSuccess("Address removed successfully!");
    } catch (error) {
      console.error("Error removing address:", error);
      setError("Failed to remove address. Please try again.");
    }
  };

  const validateForm = () => {
    if (!tempData.username.trim()) {
      setError("Username is required");
      return false;
    }

    if (!tempData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setError("Please enter a valid email address");
      return false;
    }

    if (!tempData.phone.match(/^\d{10}$/)) {
      setError("Please enter a valid 10-digit phone number");
      return false;
    }

    if (!tempData.dob) {
      setError("Date of birth is required");
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!user) {
      setError("Please log in to save your information");
      return;
    }

    if (!validateForm()) return;

    try {
      setLoading(true);
      const userDocRef = doc(db, "users", user.uid);

      await updateDoc(userDocRef, {
        ...tempData,
        lastUpdated: new Date().toISOString(),
      });

      setFormData(tempData);
      setIsEditing(false);
      setSuccess("Profile updated successfully!");
    } catch (err) {
      console.error("Error updating user data:", err);
      setError("Failed to update information. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setTempData(formData);
    setIsEditing(false);
    setIsAddingAddress(false);
    setNewAddress({
      label: "",
      street: "",
      city: "",
      state: "",
      pincode: "",
    });
    setError("");
    setSuccess("");
  };

  const personalFields = [
    { icon: User, label: "Username", key: "username" },
    { icon: Mail, label: "Email ID", key: "email" },
    { icon: Phone, label: "Phone Number", key: "phone" },
    { icon: CalendarDays, label: "Date of Birth", key: "dob", type: "date" },
  ];

  if (loading && !isEditing) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-700"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center p-8 text-gray-600">
        Please log in to view your personal information.
      </div>
    );
  }

  return (
    <div className="space-y-8 relative">
      {/* Personal Information Section */}
      <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-2xl font-medium text-purple-900">
            Personal Information
          </h3>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-purple-600 hover:text-purple-800 transition-all duration-200"
            >
              <Edit2 className="w-4 h-4" />
              <span>Edit</span>
            </button>
          ) : (
            <div className="flex space-x-2">
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-700 text-white rounded-lg hover:bg-purple-600 transition-all duration-200 disabled:opacity-50"
              >
                <Check className="w-4 h-4" />
                <span>{loading ? "Saving..." : "Save"}</span>
              </button>
              <button
                onClick={handleCancel}
                disabled={loading}
                className="flex items-center space-x-2 px-4 py-2 border border-purple-200 text-purple-700 rounded-lg hover:bg-purple-50 transition-all duration-200 disabled:opacity-50"
              >
                <X className="w-4 h-4" />
                <span>Cancel</span>
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="flex items-center space-x-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}

        {success && (
          <div className="flex items-center space-x-2 px-4 py-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
            <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
            <p>{success}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {personalFields.map(({ icon: Icon, label, key, type }) => (
            <div key={key} className="space-y-2">
              <label className="flex items-center space-x-2 text-sm font-medium text-purple-700">
                <Icon className="w-4 h-4" />
                <span>{label}</span>
              </label>
              {isEditing ? (
                <input
                  type={type || (key === "email" ? "email" : "text")}
                  name={key}
                  value={tempData[key]}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 hover:border-purple-300"
                  placeholder={`Enter your ${label.toLowerCase()}`}
                />
              ) : (
                <p className="px-4 py-2 bg-purple-50 rounded-lg text-purple-900 border border-purple-100">
                  {formData[key] || "-"}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Addresses Section */}
      <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-2xl font-medium text-purple-900">Addresses</h3>
          {!isAddingAddress && (
            <button
              onClick={() => setIsAddingAddress(true)}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-purple-600 hover:text-purple-800 transition-all duration-200"
            >
              <Plus className="w-4 h-4" />
              <span>Add Address</span>
            </button>
          )}
        </div>

        {/* Address List */}
        <div className="space-y-4">
          {addresses.map((address) => (
            <div
              key={address.id}
              className="border border-purple-100 rounded-lg p-4 space-y-2"
            >
              <div className="flex justify-between items-start">
                <span className="inline-block px-2 py-1 text-sm bg-purple-100 text-purple-700 rounded">
                  {address.label}
                </span>
                <button
                  onClick={() => handleRemoveAddress(address.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <p className="text-gray-700">{address.street}</p>
              <p className="text-gray-700">{`${address.city}, ${address.state} - ${address.pincode}`}</p>
            </div>
          ))}
        </div>

        {/* Add New Address Form */}
        {isAddingAddress && (
          <div className="border border-purple-200 rounded-lg p-4 space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="font-medium text-purple-900">Add New Address</h4>
              <button
                onClick={() => setIsAddingAddress(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-purple-700 mb-1">
                  Label
                </label>
                <select
                  name="label"
                  value={newAddress.label}
                  onChange={handleAddressChange}
                  className="w-full px-4 py-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select Label</option>
                  <option value="Home">Home</option>
                  <option value="Work">Work</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-purple-700 mb-1">
                  Street Address
                </label>
                <input
                  type="text"
                  name="street"
                  value={newAddress.street}
                  onChange={handleAddressChange}
                  className="w-full px-4 py-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter street address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-purple-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  name="city"
                  value={newAddress.city}
                  onChange={handleAddressChange}
                  className="w-full px-4 py-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter city"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-purple-700 mb-1">
                  State
                </label>
                <input
                  type="text"
                  name="state"
                  value={newAddress.state}
                  onChange={handleAddressChange}
                  className="w-full px-4 py-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter state"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-purple-700 mb-1">
                  Pincode
                </label>
                <input
                  type="text"
                  name="pincode"
                  value={newAddress.pincode}
                  onChange={handleAddressChange}
                  className="w-full px-4 py-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter 6-digit pincode"
                  maxLength={6}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={handleCancel}
                className="px-4 py-2 border border-purple-200 text-purple-700 rounded-lg hover:bg-purple-50 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleAddAddress}
                className="px-4 py-2 bg-purple-700 text-white rounded-lg hover:bg-purple-600 transition-all duration-200"
              >
                Add Address
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Decorative pattern overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          backgroundImage: `
               linear-gradient(to right, rgba(147, 51, 234, 0.05) 1px, transparent 1px),
               linear-gradient(to bottom, rgba(147, 51, 234, 0.05) 1px, transparent 1px)
             `,
          backgroundSize: "50px 50px",
        }}
      />
    </div>
  );
};

export default PersonalInfo;
